/**
 * Embeddings Utility
 * Phase 9.6: Wrapper for embedding generation using OpenAI or edge function
 */

/**
 * Generate embedding for text using the deepsearch-auto-embed edge function
 * @param {string} text - Text to embed
 * @param {string} itemId - Optional item ID (for logging)
 * @returns {Promise<number[]>} Embedding vector (1536 dimensions)
 */
export async function embedText(text, itemId = null) {
  if (!text || !text.trim()) {
    throw new Error('Text is required for embedding');
  }

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL not configured');
    }

    // Get auth session
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('Authentication required for embedding generation');
    }

    // Call edge function
    const url = `${supabaseUrl}/functions/v1/deepsearch-auto-embed`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: text.slice(0, 8000), // Limit to 8000 chars
        item_id: itemId || crypto.randomUUID(),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Embedding failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.embedding || !Array.isArray(data.embedding)) {
      throw new Error('Invalid embedding response');
    }

    if (data.embedding.length !== 1536) {
      console.warn(`⚠️ Embedding has ${data.embedding.length} dimensions, expected 1536`);
    }

    return data.embedding;
  } catch (error) {
    console.error('Error generating embedding:', error);
    throw error;
  }
}

/**
 * Generate embeddings for a batch of texts
 * @param {Array<string>} texts - Array of texts to embed
 * @param {number} delayMs - Delay between requests (default: 100ms)
 * @returns {Promise<Array<{text: string, embedding: number[]}>>} Array of text-embedding pairs
 */
export async function embedBatch(texts, delayMs = 100) {
  const results = [];
  
  for (const text of texts) {
    try {
      const embedding = await embedText(text);
      results.push({ text, embedding });
      
      // Rate limiting
      if (delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    } catch (error) {
      console.error(`Failed to embed text: ${text.slice(0, 50)}...`, error);
      // Continue with other texts
    }
  }
  
  return results;
}

/**
 * Run backfill embeddings for all items missing embeddings
 * @param {Object} options - Options for backfill
 * @param {number} options.limit - Number of items to process per batch (default: 50)
 * @param {string} options.userId - Optional user ID to filter items
 * @returns {Promise<Object>} Backfill results
 */
export async function runBackfillEmbeddings(options = {}) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  // Get auth session
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Authentication required for backfill');
  }

  const url = `${supabaseUrl}/functions/v1/backfill-embeddings`;

  console.log('[Backfill] Starting backfill request...');

  // Validate userId if provided
  let userId = options.userId || null;
  if (userId) {
    // Basic UUID validation
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      throw new Error(`Invalid userId format. Must be a valid UUID, got: ${userId}`);
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      limit: options.limit || 50,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Backfill failed: ${response.status}`);
  }

  const data = await response.json();
  console.log('[Backfill Result]', data);
  return data;
}

